import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router';
import { Plus, Save, ArrowLeft } from 'lucide-react';
import {
  listMcpConfigs,
  getMcpConfig,
  saveMcpConfig,
  deleteMcpConfig,
  type McpConfigResponse,
  type McpConfigSummary,
} from '@/lib/api';
import { ResourceDeleteConfirm } from '@/components/common/ResourceDeleteConfirm';

interface McpConfigsPageProps {
  routePrefix?: string;
}

export function McpConfigsPage({
  routePrefix = '/admin/resources/mcp-configs',
}: McpConfigsPageProps): React.ReactElement {
  const { name } = useParams<{ name?: string }>();
  return name ? (
    <McpConfigEditor name={name} routePrefix={routePrefix} />
  ) : (
    <McpConfigListView routePrefix={routePrefix} />
  );
}

function McpConfigListView({ routePrefix }: { routePrefix: string }): React.ReactElement {
  const [configs, setConfigs] = useState<McpConfigSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    listMcpConfigs()
      .then(r => {
        setConfigs(r.mcpConfigs);
      })
      .catch((e: Error) => {
        setError(e.message);
      });
  }, []);

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <div className="flex items-center justify-between px-4 pt-4 pb-2">
        <h1 className="text-lg font-semibold text-text-primary">MCP Configs</h1>
        <Link
          to={`${routePrefix}/new`}
          className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-white hover:bg-primary/90"
        >
          <Plus className="size-4" />
          New MCP Config
        </Link>
      </div>
      <div className="flex-1 overflow-auto px-4 pb-4 pt-2">
        {error && <div className="rounded-md bg-error/15 p-3 text-sm text-error">{error}</div>}
        {!configs && !error && <div className="text-sm text-text-tertiary">Loading…</div>}
        {configs?.length === 0 && (
          <div className="text-sm text-text-tertiary">No MCP configs yet.</div>
        )}
        {configs && configs.length > 0 && (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-text-secondary">
                <th className="py-2 pr-4">Name</th>
                <th className="py-2 pr-4">Description</th>
                <th className="py-2 pr-4">Servers</th>
                <th className="py-2 pr-4">Source</th>
                <th className="py-2">Updated</th>
              </tr>
            </thead>
            <tbody>
              {configs.map(c => (
                <tr key={c.name} className="border-b border-border hover:bg-surface/50">
                  <td className="py-2 pr-4">
                    <Link
                      to={`${routePrefix}/${encodeURIComponent(c.name)}`}
                      className="text-primary"
                    >
                      {c.name}
                    </Link>
                  </td>
                  <td className="py-2 pr-4 text-text-secondary">
                    {c.description || <span className="text-text-muted">None</span>}
                  </td>
                  <td className="py-2 pr-4 text-text-secondary">{c.serverNames.join(', ')}</td>
                  <td className="py-2 pr-4">
                    <span className="rounded bg-surface-elevated px-2 py-0.5 text-xs">
                      {c.source}
                    </span>
                  </td>
                  <td className="py-2 text-text-tertiary">{c.updatedAt.slice(0, 19)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function McpConfigEditor({
  name,
  routePrefix,
}: {
  name: string;
  routePrefix: string;
}): React.ReactElement {
  const navigate = useNavigate();
  const isNew = name === 'new';
  const [config, setConfig] = useState<McpConfigResponse | null>(null);
  const [configName, setConfigName] = useState(isNew ? '' : name);
  const [description, setDescription] = useState('');
  const [jsonText, setJsonText] = useState(
    isNew ? '{\n  "server-name": {\n    "command": "npx"\n  }\n}\n' : ''
  );
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isNew) return;
    getMcpConfig(name)
      .then(c => {
        setConfig(c);
        setDescription(c.description ?? '');
        setJsonText(JSON.stringify(c.configJson, null, 2));
      })
      .catch((e: Error) => {
        setError(e.message);
      });
  }, [name, isNew]);

  const isBundled = config?.source === 'bundled';

  async function handleSave(): Promise<void> {
    if (!configName) {
      setError('Name is required');
      return;
    }
    let parsed: Record<string, unknown>;
    try {
      parsed = JSON.parse(jsonText);
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        throw new Error('configJson must be a JSON object');
      }
    } catch (e) {
      setError('Invalid JSON: ' + (e as Error).message);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const saved = await saveMcpConfig(configName, {
        description: description || null,
        configJson: parsed,
      });
      setConfig(saved);
      if (isNew) navigate(`${routePrefix}/${encodeURIComponent(configName)}`);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(): Promise<void> {
    try {
      await deleteMcpConfig(configName);
      navigate(routePrefix);
    } catch (e) {
      setError((e as Error).message);
    }
  }

  let serverNames: string[] = [];
  try {
    const parsed = JSON.parse(jsonText);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      serverNames = Object.keys(parsed);
    }
  } catch {
    // ignore invalid JSON — the summary just won't show servers
  }

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <div className="flex items-center gap-2 border-b border-border px-4 py-2">
        <Link to={routePrefix} className="text-text-secondary hover:text-text-primary">
          <ArrowLeft className="size-4" />
        </Link>
        <input
          className="w-48 rounded bg-surface px-2 py-1 text-sm"
          value={configName}
          onChange={e => {
            setConfigName(e.target.value);
          }}
          placeholder="config-name"
          disabled={!isNew}
        />
        <input
          className="flex-1 rounded bg-surface px-2 py-1 text-sm"
          value={description}
          onChange={e => {
            setDescription(e.target.value);
          }}
          placeholder="Description"
          disabled={isBundled}
        />
        <button
          onClick={handleSave}
          disabled={saving || isBundled}
          className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1 text-sm text-white disabled:opacity-50"
        >
          <Save className="size-4" /> Save
        </button>
        {!isNew && (
          <ResourceDeleteConfirm
            resourceKind="MCP config"
            resourceName={configName}
            disabled={isBundled}
            onConfirm={handleDelete}
          />
        )}
      </div>
      {error && (
        <div className="border-b border-error/30 bg-error/15 px-4 py-2 text-sm text-error">
          {error}
        </div>
      )}
      {serverNames.length > 0 && (
        <div className="border-b border-border px-4 py-2 text-xs text-text-tertiary">
          Servers: {serverNames.join(', ')}
        </div>
      )}
      <textarea
        className="flex-1 resize-none bg-background p-4 font-mono text-sm text-text-primary outline-none"
        value={jsonText}
        onChange={e => {
          setJsonText(e.target.value);
        }}
        readOnly={isBundled}
      />
    </div>
  );
}
